import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { AdministrationItemType, DetailsInitInfo, ExtendedDetailsInitInfo, IExtendedTreeNodeData, IItemData, ITreeNodeData, ItemEventInfo, ParentInfo } from "../data-structures";
import { DragAndDropEventArgs, NodeSelectEventArgs, TreeViewComponent } from "@syncfusion/ej2-angular-navigations";
import { ClientService } from "src/app/base/services/client/client.service";
import { ItemTypeUtil } from "../item-type.util";
import { iconTextCodes } from "./icon-text-codes";
import { ZoneDTO } from "src/app/base/models/models.swagger.generated";
import { Observable, map, merge, of } from "rxjs";
import { ModalService } from "src/app/base/shared/modal/modal.service";

@Component({ template: '' })
export abstract class TreeViewBaseComponent {
  @Input() public hasPendingDetailChanges: boolean = false;
  @Output() public itemSelected = new EventEmitter<DetailsInitInfo>();

  @ViewChild('tree') public tree: TreeViewComponent;

  public iconTextCodes = iconTextCodes;

  protected selectedItem: ExtendedDetailsInitInfo = null;
  protected parentInfo: ParentInfo = null;
  protected isSupportingCopyMode = true;
  protected isSupportingMoveMode = true;

  constructor(
    protected clientService: ClientService,
    protected modalService: ModalService) { }

  public getIconClassByType(type: AdministrationItemType): string {
    return ItemTypeUtil.getIconClassByType(type);
  }

  public hasIcon(type: AdministrationItemType): boolean {
    return !!ItemTypeUtil.getIconClassByType(type);
  }

  public reset() {
    this.tree.refresh();
    this.resetSelection();
  }

  public resetSelection(): void {
    this.setSelectedItem(null);
    this.tree.selectedNodes = [];
  }

  public onCollapseAll(): void {
    this.canDeactivate().subscribe((canDeactivate) => {
      if (!canDeactivate) {
        return;
      }
      this.tree.collapseAll();
      this.resetSelection();
    });
  }

  public onNodeSelected(args: NodeSelectEventArgs): void {
    this.canDeactivate().subscribe((canDeactivate) => {
      if (canDeactivate) {
        this.parentInfo = null;
        let newSelectedItem: ExtendedDetailsInitInfo = null;
        if (args.nodeData && args.action === 'select') {
          newSelectedItem = this.parseNodeId(args.nodeData.id as string) as DetailsInitInfo;
          newSelectedItem.clientId = this.clientService.selectedClient?.id;
          newSelectedItem.itemData = this.tree.getTreeData(args.nodeData.id as string)?.at(0)?.itemData as IItemData;
          newSelectedItem.treeNodeId = args.nodeData.id as string;
          if (args.nodeData.parentID) {
            const parentData = this.parseNodeId(args.nodeData.parentID as string);
            newSelectedItem.parentId = parentData.id;
            newSelectedItem.parentType = parentData.type;
            newSelectedItem.uniqueIdent = parentData.uniqueIdent;
          } else {
            newSelectedItem.parentId = null;
            newSelectedItem.parentType = null;
            newSelectedItem.uniqueIdent = null;
          }
        }
        this.setSelectedItem(newSelectedItem);
      } else {
        this.tree.selectedNodes = [this.selectedItem.treeNodeId];
      }
    });
  }

  public onNodeDragStop(args: DragAndDropEventArgs): void {
    // somehow on nodeDragStop the position is not delivered as inside, but as before or after
    if ((args.position != "Inside" && args.position != "After") || args.dropIndicator == "e-no-drop" || args.draggedNodeData.id === args.droppedNodeData.id) {
      args.cancel = true;
      return;
    }
    if (!this.canDropNode(args)) {
      args.cancel = true;
    }
  }

  public onNodeDragging(args: DragAndDropEventArgs) {
    const isCopyMode = this.isCopyMode(args);
    if (args.position != "Inside") {
      args.dropIndicator = 'e-no-drop';
      return;
    }
    if (!this.canDropNode(args)) {
      args.dropIndicator = 'e-no-drop';
      return;
    }

    if (!isCopyMode) {
      args.dropIndicator = 'e-sorting-3';
    }
  }

  public abstract onNodeDragStart(args: DragAndDropEventArgs);

  protected abstract parseNodeId(nodeId: string): DetailsInitInfo;
  protected abstract canDropNode(args: DragAndDropEventArgs): boolean;

  protected moveNode(nodeId: string, parentId: string): void {
    const nodeData = this.tree.getTreeData(nodeId)?.at(0) as any as ITreeNodeData;
    const parentNodeData = this.tree.getTreeData(parentId)?.at(0);
    const childNodes = parentNodeData?.child as ITreeNodeData[];
    if (parentNodeData.hasChildren && !parentNodeData.expanded && (!childNodes || childNodes.length === 0)) {
      this.tree.removeNodes([nodeId]);
      this.tree.expandAll([parentId]);
      return;
    }

    const nodeExists = !!childNodes?.find((node) => node.itemData.type === nodeData.itemData.type && node.itemData.dto.id === nodeData.itemData.dto.id);
    if (nodeExists) {
      // remove on old branch
      this.tree.removeNodes([nodeId]);
      return;
    }

    const index = this.getInsertPositionIndex(nodeData, parentNodeData?.child as ITreeNodeData[]);
    this.tree.moveNodes([nodeId], parentId, index);
  }

  protected canDeactivate(): Observable<boolean> {
    if (this.selectedItem && this.hasPendingDetailChanges) {
      const dialog = this.modalService.showOkCancelDialog('i18n.errors.discard-dialog.title', 'i18n.errors.discard-dialog.question');
      return merge(
        dialog.onOkSubject.pipe(map(() => true)),
        dialog.onCancelSubject.pipe(map(() => false)),
      );
    }
    return of(true);
  }

  protected handleAddNodeEvent(eventInfo: ItemEventInfo, nodeId: string): ITreeNodeData {
    const parentNodeData = this.tree.getTreeData(this.parentInfo.nodes[0])?.at(0);
    if (!parentNodeData) {
      return null;
    }

    if (parentNodeData.hasChildren && !parentNodeData.child && (parentNodeData.itemData as IItemData).type !== AdministrationItemType.ConfigBasedStructuralElementCollection
    && (parentNodeData.itemData as IItemData).type !== AdministrationItemType.ZoneCollection) {
      const selectedNode = this.tree.selectedNodes?.at(0);
      if (selectedNode && this.parentInfo.nodes[0] === selectedNode) {
        this.setSelectedItem(this.parentInfo.item);
        this.parentInfo = null;
      }
      this.tree.expandAll([parentNodeData.id as string]);
      return null;
    }

    const createdItem: ITreeNodeData = {
      id: nodeId,
      hasChildren: this.hasChildrenByDefault(eventInfo),
      itemData: {
        type: eventInfo.type,
        inactive: (eventInfo.data as any)?.active != null ? !(eventInfo.data as any).active : false,
        name: eventInfo.data.name,
        dto: eventInfo.data,
      }
    }

    this.addNodeAtProperPosition(createdItem, parentNodeData);

    const selectedNode = this.tree.selectedNodes?.at(0);
    if (selectedNode && this.parentInfo.nodes[0] === selectedNode) {
      this.setSelectedItem(this.parentInfo.item);
      this.parentInfo = null;
    }
    return createdItem;
  }

  protected addNodeAtProperPositionByParentNodeId(node: ITreeNodeData, parentNodeId: string): void {
    const parentNode = this.tree.getTreeData(parentNodeId)?.at(0);
    if (!parentNode) {
      return;
    }

    const childNodes = parentNode?.child as ITreeNodeData[];
    if (parentNode.hasChildren && !parentNode.expanded && (!childNodes || childNodes.length === 0)) {
      this.tree.expandAll([parentNodeId]);
      return;
    }
    this.addNodeAtProperPosition(node, parentNode);
  }

  protected addNodeAtProperPosition(node: ITreeNodeData, parentNode: {[key: string]: Object;}): void {
    // find proper index where to add new node
    const index = this.getInsertPositionIndex(node, parentNode?.child as ITreeNodeData[]);
    // working directly on tree data does not work -> so we have to add node and then fix for shared data
    this.tree.addNodes([node as any], parentNode.id as string, index);

    // add node data fix -> since this.tree.addNodes makes a deep copy of the node we need to reapply shared data
    setTimeout(() => {
      const nodeData = this.tree.getTreeData(node.id)?.at(0);
      if (nodeData) {
        nodeData.itemData = node.itemData;
      }
    }, 0);
  }

  protected getInsertPositionIndex(node: ITreeNodeData, children: ITreeNodeData[]): number {
    const childCount = children?.length ?? 0;
    let i = 0;
    for (i = 0; i < childCount; i++) {
      const child = children[i];
      const compareResult = this.itemDataCompare(node.itemData, child.itemData);
      if (compareResult < 0) {
        break;
      }
    }
    return i;
  }

  protected hasChildrenByDefault(eventInfo: ItemEventInfo): boolean {
    if (eventInfo.type === AdministrationItemType.Zone) {
      return true;
    }
    return false;
  }

  protected itemDataCompare(a: IItemData, b: IItemData): number {
    if (a.type !== b.type) {
      // Zone item is the only item with different type enum value as children
      return a.type - b.type; // works only as long as acutal type enum values are kept the same
    }
    return a.name.localeCompare(b.name);
  }

  protected handleRemoveNodeEvent(eventInfo: ItemEventInfo): void {
    const nodesToDelete = this.findAllNodesOfTree(eventInfo.id, eventInfo.type);
    nodesToDelete.forEach((node) => {
      // as of today (2021-03-10) there is a bug in treeView, that it does not remove node from selectedNodes when passing all nodes at once
      this.tree.removeNodes([node.id]);
    });
    this.setSelectedItem(null);
    this.tree.selectedNodes = [];
  }

  protected handleUpdateNodeEvent(eventInfo: ItemEventInfo): void {
    if (this.selectedItem?.id !== eventInfo?.id) {
      return;
    }
    const data = this.tree.getTreeData(this.selectedItem.treeNodeId);
    if (data && data.length > 0) {
      const itemData = data[0].itemData as IItemData;
      itemData.name = eventInfo.data.name;
      itemData.dto = eventInfo.data;
      itemData.inactive = this.isItemInactive(eventInfo);
    }
    this.updateCanAddXyFlags();
  }

  protected abstract isItemInactive(eventInfo: ItemEventInfo): boolean;
  protected abstract updateCanAddXyFlags(): void;

  protected onAddNewItem(type: AdministrationItemType) {
    this.parentInfo = { item: this.selectedItem, nodes: this.tree.selectedNodes };
    this.setSelectedItem({ id: 0, type, parentId: this.parentInfo.item.id, parentType: this.parentInfo.item.type, clientId: this.clientService.selectedClient?.id });
  }

  protected setSelectedItem(item: ExtendedDetailsInitInfo) {
    if (this.selectedItem?.itemData) {
      this.selectedItem.itemData.isHighlighted = false;
    }
    this.selectedItem = item;
    if (this.selectedItem?.itemData) {
      // highlights all items with same shared data
      this.selectedItem.itemData.isHighlighted = true;
    }
    this.updateCanAddXyFlags();
    if (this.selectedItem) {
      this.itemSelected.emit({id: item.id, type: item.type, parentId: item.parentId, parentType: item.parentType, clientId: item.clientId, uniqueIdent: item.uniqueIdent});
    } else {
      this.itemSelected.emit(null);
    }
  }

  protected findAllNodesOfTree(id: number, type: AdministrationItemType): IExtendedTreeNodeData[] {
    const nodes: IExtendedTreeNodeData[] = [];
    const rootNodes = this.tree.getTreeData();
    rootNodes.forEach((node) => {
      this.findTopNodes(node as any, id, type, nodes);
    });
    return nodes;
  }

  protected findTopNodes(node: IExtendedTreeNodeData, itemId: number, type: AdministrationItemType, matchingNodes: IExtendedTreeNodeData[]) {
    if (node.itemData.dto?.id === itemId && node.itemData.type === type) {
      matchingNodes.push(node);
      return;
    }
    (node as any).child?.map((child: any) => {
      // child nodes do not have parentId, so we have to add it
      this.findTopNodes({...child, parentId: node.id}, itemId, type, matchingNodes);
    });
  }

  protected isCopyMode(args: DragAndDropEventArgs): boolean {
    if (!this.isSupportingMoveMode) {
      return this.isSupportingCopyMode;
    }
    return (this.isSupportingCopyMode && (args.event.altKey || args.event.ctrlKey || args.event.shiftKey));
  }

  protected setMoveIndicator(args: DragAndDropEventArgs) {
    args.dropIndicator = 'e-sorting-3';
  }
}
